import { useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { placePanel } from '../lib/placement'

/**
 * The floating panel under a dropdown, date picker or menu.
 *
 * It renders into document.body with fixed positioning, so no scrolling area,
 * card, table or dialog can clip it and nothing later in the page can paint
 * over it. It opens above the control when there is more room there, scrolls
 * inside the space it has, follows the control as the page scrolls, and closes
 * once the control scrolls out of sight.
 *
 * It also owns the behaviour every such panel needs: a click outside or
 * Escape closes it (Escape goes no further, so a dropdown inside a dialog does
 * not close the dialog too; an input marked `data-escape-local` handles its
 * own Escape), the arrow keys, Home and End move between the
 * options marked `data-option`, Enter in a `data-popover-search` box picks the
 * first option, and focus goes back to the control when the panel closes.
 */

interface PopoverProps {
  open: boolean
  /** The control the panel hangs off. Clicks inside it are not outside clicks. */
  anchorRef: RefObject<HTMLElement>
  onClose: () => void
  children: ReactNode
  className?: string
  /** 'anchor' gives the panel the control's width, 'min-anchor' at least that
   *  width, and 'content' leaves the width to className. */
  width?: 'anchor' | 'min-anchor' | 'content'
  align?: 'start' | 'end'
  /** Accessible name for the panel, e.g. "Owner options". */
  label?: string
}

const OPTION = '[data-option]:not([disabled])'

export default function Popover({
  open, anchorRef, onClose, children, className = '', width = 'min-anchor', align = 'start', label,
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const clickedOutside = useRef(false)
  // The latest onClose without re-subscribing every listener on each render.
  const close = useRef(onClose)
  close.current = onClose

  // Position before paint, then keep up with scrolling, resizing and the
  // panel's own content changing size (a search narrowing the list).
  useLayoutEffect(() => {
    if (!open) return
    const panel = panelRef.current
    const anchor = anchorRef.current
    if (!panel || !anchor) return

    let frame = 0
    const place = () => {
      const rect = anchor.getBoundingClientRect()
      const offScreen = rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth
      if (offScreen) {
        close.current()
        return
      }
      panel.style.maxHeight = 'none'
      if (width === 'anchor') panel.style.width = `${rect.width}px`
      if (width === 'min-anchor') panel.style.minWidth = `${rect.width}px`
      panel.style.maxWidth = `${window.innerWidth - 16}px`
      const p = placePanel(rect, { width: panel.offsetWidth, height: panel.offsetHeight }, { width: window.innerWidth, height: window.innerHeight }, align)
      panel.style.left = `${p.left}px`
      panel.style.top = p.side === 'below' ? `${p.offset}px` : ''
      panel.style.bottom = p.side === 'above' ? `${p.offset}px` : ''
      panel.style.maxHeight = `${p.maxHeight}px`
      panel.style.opacity = '1'
      panel.style.pointerEvents = 'auto'
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(place)
    }

    place()
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
    observer?.observe(panel)
    observer?.observe(anchor)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, true)
      observer?.disconnect()
    }
  }, [open, anchorRef, width, align])

  // Move focus in: the search box if there is one, otherwise the selected
  // option or the first, which also scrolls a long list to the current choice.
  useLayoutEffect(() => {
    if (!open) return
    const panel = panelRef.current
    if (!panel || panel.contains(document.activeElement)) return
    const search = panel.querySelector<HTMLElement>('[data-popover-search]')
    if (search) {
      search.focus({ preventScroll: true })
      return
    }
    const target = panel.querySelector<HTMLElement>(`${OPTION}[aria-selected="true"]`) ?? panel.querySelector<HTMLElement>(OPTION)
    target?.focus({ preventScroll: true })
    target?.scrollIntoView({ block: 'nearest' })
  }, [open])

  // Hand focus back to the control when the panel closes with focus inside it,
  // so a keyboard user carries on from where they were. By the time this runs
  // the panel is gone, so focus that was inside it has fallen to the page body.
  // A click outside moves focus to what was clicked and is left alone.
  useEffect(() => {
    if (!open) return
    clickedOutside.current = false
    return () => {
      const lost = !document.activeElement || document.activeElement === document.body
      if (clickedOutside.current || !lost) return
      const anchor = anchorRef.current
      const control = anchor?.matches('button, [tabindex]') ? anchor : anchor?.querySelector<HTMLElement>('button, [tabindex]')
      control?.focus({ preventScroll: true })
    }
  }, [open, anchorRef])

  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return
      clickedOutside.current = true
      close.current()
    }
    const onKey = (e: KeyboardEvent) => {
      const panel = panelRef.current
      if (!panel) return
      if (e.key === 'Escape') {
        // An inline editor in the panel (renaming a saved view) cancels its
        // own edit on Escape rather than closing the whole panel.
        if ((e.target as Element).closest?.('[data-escape-local]')) return
        e.stopPropagation()
        e.preventDefault()
        close.current()
        return
      }
      if (!panel.contains(document.activeElement)) return
      if (e.key === 'Tab') {
        // The panel sits at the end of the page, so tabbing on from it would
        // leave the form. Close and return to the control instead.
        e.preventDefault()
        close.current()
        return
      }
      const options = [...panel.querySelectorAll<HTMLElement>(OPTION)]
      const active = document.activeElement as HTMLElement
      const inSearch = active.matches('[data-popover-search]')
      if (inSearch && e.key === 'Enter') {
        e.preventDefault()
        options[0]?.click()
        return
      }
      if (!options.length || (active.matches('input, textarea') && !inSearch)) return
      const index = options.indexOf(active)
      // A grid of options (a calendar) sets data-columns, so up and down move
      // a row and left and right move one option.
      const columns = Number(panel.querySelector('[data-columns]')?.getAttribute('data-columns')) || 1
      const step = (by: number) => options[Math.max(0, Math.min(index + by, options.length - 1))]
      const next =
        e.key === 'ArrowDown' ? (inSearch || index < 0 ? options[0] : step(columns))
        : e.key === 'ArrowUp' ? (index <= 0 ? null : index - columns < 0 ? options[index] : step(-columns))
        : e.key === 'ArrowRight' && columns > 1 ? step(1)
        : e.key === 'ArrowLeft' && columns > 1 ? step(-1)
        : e.key === 'Home' ? options[0]
        : e.key === 'End' ? options[options.length - 1]
        : undefined
      if (next === undefined) return
      e.preventDefault()
      if (next === null) {
        // Up from the first option goes back to the search box, if there is one.
        panel.querySelector<HTMLElement>('[data-popover-search]')?.focus()
        return
      }
      next.focus()
      next.scrollIntoView({ block: 'nearest' })
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, anchorRef])

  if (!open) return null

  return createPortal(
    <div
      ref={panelRef}
      data-popover=""
      aria-label={label}
      // Transparent rather than hidden until placed: a hidden element cannot
      // take focus, which would stop a search box focusing as the panel opens.
      style={{ position: 'fixed', top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}
      className={`z-[60] flex flex-col overflow-hidden bg-white border border-slate-200 rounded-xl shadow-lg ${className}`}
    >
      {children}
    </div>,
    document.body,
  )
}
